import type {
  MemberVehicle,
  ScheduledCarrierCreateData,
  ScheduledCarrierUpdateData,
} from "@/pages/carrier-cargo-detail/model";
import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseIcon } from "@/shared/ui/BaseIcons";
import { BaseDialog } from "@/shared/ui/base-dialog";
import { BaseFormLabel } from "@/shared/ui/base-form-label";
import { BaseTextField } from "@/shared/ui/base-text-field";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Autocomplete,
  Box,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { FreightMatching } from "@x-mile/api-contract";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

const scheduledCarrierFormSchema = z.object({
  vehicleNumber: z.string().min(1, "車両ナンバーを入力してください"),
  name: z.string().optional(),
  contactName: z.string().optional(),
  contactNameKana: z.string().optional(),
  phoneNumber: z.string().optional(),
});

type FormData = z.infer<typeof scheduledCarrierFormSchema>;

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    data: ScheduledCarrierCreateData | ScheduledCarrierUpdateData,
  ) => Promise<void>;
  editingData?: FreightMatching.schemas.OfferCargo["scheduledCarrier"];
  vehicles: MemberVehicle[];
  isSubmitting: boolean;
  mode: "create" | "edit";
};

export const ScheduledCarrierFormDialog = ({
  open,
  onClose,
  onSubmit,
  editingData,
  vehicles,
  isSubmitting,
  mode,
}: Props) => {
  const { control, handleSubmit, reset, setValue } = useForm<FormData>({
    resolver: zodResolver(scheduledCarrierFormSchema),
    defaultValues: {
      vehicleNumber: editingData?.vehicleNumber || "",
      name: editingData?.name || "",
      contactName: editingData?.contactName || "",
      contactNameKana: editingData?.contactNameKana || "",
      phoneNumber: editingData?.phoneNumber || "",
    },
  });

  // 編集データが変更されたときにフォームの値を更新
  useEffect(() => {
    if (open && editingData) {
      reset({
        vehicleNumber: editingData.vehicleNumber || "",
        name: editingData.name || "",
        contactName: editingData.contactName || "",
        contactNameKana: editingData.contactNameKana || "",
        phoneNumber: editingData.phoneNumber || "",
      });
    }
  }, [open, editingData, reset]);

  const handleFormSubmit = async (data: FormData) => {
    await onSubmit(data);
    reset();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleVehicleSelect = (vehicle: MemberVehicle | null) => {
    if (vehicle?.number) {
      setValue("vehicleNumber", vehicle.number);
    }
  };

  return (
    <BaseDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === "create" ? "配送予定車両を登録" : "配送予定車両を編集"}
      </DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit(handleFormSubmit)}>
          <Stack spacing={3} mt={1}>
            <BaseFormLabel label="車両ナンバー" isRequired>
              <Controller
                name="vehicleNumber"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl fullWidth error={fieldState.invalid}>
                    <Autocomplete
                      freeSolo
                      value={field.value}
                      onChange={(_, newValue) => {
                        if (typeof newValue === "string") {
                          field.onChange(newValue);
                        } else if (newValue) {
                          handleVehicleSelect(newValue);
                        }
                      }}
                      onInputChange={(_, newInputValue) => {
                        field.onChange(newInputValue);
                      }}
                      options={vehicles}
                      getOptionLabel={(option) =>
                        typeof option === "string"
                          ? option
                          : option.number || ""
                      }
                      isOptionEqualToValue={(option, value) =>
                        typeof option === "string"
                          ? option === value
                          : option.number === value
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={fieldState.invalid}
                          placeholder="車両ナンバーを入力または選択してください"
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Stack>
                            <Typography variant="body2" fontWeight={600}>
                              {option.number} - {option.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {option.shapeText} | {option.weight}kg
                            </Typography>
                          </Stack>
                        </Box>
                      )}
                    />
                    {fieldState.error && (
                      <FormHelperText>
                        {fieldState.error.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </BaseFormLabel>

            <BaseFormLabel label="実運送会社名">
              <Controller
                name="name"
                control={control}
                render={({ field, fieldState }) => (
                  <BaseTextField
                    {...field}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                    fullWidth
                  />
                )}
              />
            </BaseFormLabel>

            <BaseFormLabel label="運転手名">
              <Controller
                name="contactName"
                control={control}
                render={({ field, fieldState }) => (
                  <BaseTextField
                    {...field}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                    fullWidth
                  />
                )}
              />
            </BaseFormLabel>

            <BaseFormLabel label="運転手名（カナ）">
              <Controller
                name="contactNameKana"
                control={control}
                render={({ field, fieldState }) => (
                  <BaseTextField
                    {...field}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                    fullWidth
                  />
                )}
              />
            </BaseFormLabel>

            <BaseFormLabel label="電話番号">
              <Controller
                name="phoneNumber"
                control={control}
                render={({ field, fieldState }) => (
                  <BaseTextField
                    {...field}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                    fullWidth
                  />
                )}
              />
            </BaseFormLabel>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <BaseButton
          variant="outlined"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          キャンセル
        </BaseButton>
        <BaseButton
          variant="contained"
          onClick={handleSubmit(handleFormSubmit)}
          disabled={isSubmitting}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={16} />
            ) : (
              <BaseIcon name="check" />
            )
          }
        >
          {mode === "create" ? "登録" : "更新"}
        </BaseButton>
      </DialogActions>
    </BaseDialog>
  );
};
